import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useKeycloak } from "./auth/keycloakProvider";
import { WalletProvider } from "./contexts/WalletContext";
import { MainLayout } from "./components/layout/MainLayout";

import Index from "./pages/Index";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import CompanyForm from "./pages/CompanyForm";
import Employees from "./pages/Employees";
import EmployeeForm from "./pages/EmployeeForm";
import EmployeeDetail from "./pages/EmployeeDetail";
import ECMRList from "./pages/ECMRList";
import ECMRDetail from "./pages/ECMRDetail";
import ECMRAdd from "./pages/ECMRAdd";
import ECMRSign from "./pages/ECMRSign";
import NotFound from "./pages/NotFound";
import { TransportNoteDetails } from "./components/deliveries/TransportNoteDetails";

const queryClient = new QueryClient();

// Protected route component using Keycloak
function ProtectedRoute({ children, adminOnly = false, companyAdminAllowed = false }: { children: React.ReactNode; adminOnly?: boolean; companyAdminAllowed?: boolean }) {
    const { isAuthenticated, loading, isSystemAdmin, isCompanyAdministrator, requiresWalletSetup } = useKeycloak();
    const location = useLocation();

    //console.log('ProtectedRoute - loading:', loading, 'isAuthenticated:', isAuthenticated, 'requiresWalletSetup:', requiresWalletSetup);

    // Show loading while Keycloak initializes
    if (loading) {
        return <div className="flex items-center justify-center h-screen">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-gray-600">Initializing authentication...</p>
            </div>
        </div>;
    }

    if (!isAuthenticated) {
        //console.log('ProtectedRoute - Not authenticated, redirecting to /login');
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // Check if wallet setup is required - redirect to login which will show wallet setup
    if (requiresWalletSetup) {
        //console.log('ProtectedRoute - Wallet setup required, redirecting to /login');
        return <Navigate to="/login" replace />;
    }

    // Check admin access
    if (adminOnly && !isSystemAdmin && !(companyAdminAllowed && isCompanyAdministrator)) {
        //console.log('ProtectedRoute - Admin only, user not admin, redirecting to /ecmrs');
        return <Navigate to="/ecmrs" replace />;
    }

    return <MainLayout>{children}</MainLayout>;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />

            {/* Admin Only Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute adminOnly>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/companies/new"
                element={
                    <ProtectedRoute adminOnly>
                        <CompanyForm />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/employees"
                element={
                    <ProtectedRoute adminOnly>
                        <Employees />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/employees/new"
                element={
                    <ProtectedRoute adminOnly companyAdminAllowed>
                        <EmployeeForm />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/employees/:id"
                element={
                    <ProtectedRoute adminOnly companyAdminAllowed>
                        <EmployeeDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/employees/:id/edit"
                element={
                    <ProtectedRoute adminOnly companyAdminAllowed>
                        <EmployeeForm />
                    </ProtectedRoute>
                }
            />

            {/* Shared Routes */}
            <Route
                path="/companies"
                element={
                    <ProtectedRoute>
                        <Companies />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/companies/:id"
                element={
                    <ProtectedRoute>
                        <CompanyDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/companies/:id/edit"
                element={
                    <ProtectedRoute>
                        <CompanyForm />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ecmrs"
                element={
                    <ProtectedRoute>
                        <ECMRList />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ecmrs/new"
                element={
                    <ProtectedRoute>
                        <ECMRAdd />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ecmrs/:id"
                element={
                    <ProtectedRoute>
                        <ECMRDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ecmrs/:id/sign"
                element={
                    <ProtectedRoute>
                        <ECMRSign />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/transport-notes/:noteId"
                element={
                    <ProtectedRoute>
                        <TransportNoteDetails />
                    </ProtectedRoute>
                }
            />

            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

const App = () => (
    <QueryClientProvider client={queryClient}>
        <TooltipProvider>
            <WalletProvider>
                <Toaster />
                <Sonner />
                <AppRoutes />
            </WalletProvider>
        </TooltipProvider>
    </QueryClientProvider>
);

export default App;